import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMe } from '../lib/auth.js';
import { cartReadiness, useCart } from '../lib/cart.js';
import { useCheckout } from '../lib/order.js';
import { ApiError } from '../lib/api.js';
import { bindingTypeLabel } from '@z9-store/core/labels';

export function CartPage(): JSX.Element {
  const me = useMe();
  const cart = useCart();
  const checkout = useCheckout();
  const readiness = cartReadiness(cart.items);

  if (me.isLoading) return <p className="text-sm text-slate-500">Loading...</p>;
  if (!me.data) {
    return (
      <div data-testid="cart-signin" className="rounded bg-slate-100 p-6 text-sm text-slate-700">
        <Link to="/login?next=%2Fcart" className="font-medium text-slate-900 underline">
          Sign in
        </Link>{' '}
        to check out.
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div data-testid="cart-empty" className="rounded bg-slate-100 p-6 text-sm text-slate-700">
        Your cart is empty.{' '}
        <Link to="/" className="font-medium text-slate-900 underline">
          Browse the catalog
        </Link>
        .
      </div>
    );
  }

  const onCheckout = async () => {
    const r = await checkout.mutateAsync(cart.items);
    window.location.assign(r.checkoutUrl);
  };

  return (
    <div data-testid="cart" className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">Your cart</h1>
      <ul className="space-y-4">
        {cart.items.map((item) => (
          <li
            key={item.skuId}
            data-testid={`cart-item-${item.skuId}`}
            className="space-y-3 rounded-lg border border-slate-200 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="text-base font-semibold text-slate-900">{item.displayName}</div>
              <button
                type="button"
                onClick={() => cart.remove(item.skuId)}
                className="text-sm text-slate-500 hover:text-red-600"
                data-testid={`cart-remove-${item.skuId}`}
              >
                Remove
              </button>
            </div>
            {item.bindingType === 'FINGERPRINT_HEX' ? (
              <SystemFingerprintField
                skuId={item.skuId}
                bindingValue={item.bindingValue}
                onSet={(hex) => cart.updateBinding(item.skuId, hex)}
              />
            ) : (
              <label className="block text-sm">
                <span className="font-medium text-slate-700">{bindingTypeLabel(item.bindingType)}</span>
                <input
                  type="text"
                  value={item.bindingValue}
                  onChange={(e) => cart.updateBinding(item.skuId, e.target.value)}
                  placeholder={item.bindingType === 'MAC_ADDRESS' ? 'aa:bb:cc:dd:ee:ff' : '00000000-0000-0000-0000-000000000000'}
                  className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                  data-testid={`cart-binding-${item.skuId}`}
                  required
                />
              </label>
            )}
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Deployment notes (optional)</span>
              <input
                type="text"
                value={item.deploymentNotes}
                onChange={(e) => cart.updateNotes(item.skuId, e.target.value)}
                placeholder="e.g. Acme HQ -- 50 doors -- 2026 install"
                className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                data-testid={`cart-notes-${item.skuId}`}
              />
            </label>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-end gap-3">
        {readiness ? <span className="text-sm text-slate-500" data-testid="cart-readiness">{readiness}</span> : null}
        <button
          type="button"
          onClick={onCheckout}
          disabled={Boolean(readiness) || checkout.isPending}
          className="rounded bg-slate-900 px-5 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          data-testid="cart-checkout"
        >
          {checkout.isPending ? 'Redirecting...' : 'Checkout'}
        </button>
      </div>
      {checkout.error ? (
        <div className="rounded bg-red-50 p-3 text-sm text-red-700" data-testid="cart-error">
          {checkout.error instanceof ApiError ? checkout.error.message : checkout.error.message}
        </div>
      ) : null}
    </div>
  );
}

// The system fingerprint is normally a file the dealer downloads from the
// Z9/Flex Server install -- they shouldn't have to open it and paste the
// hex. We read the file client-side, trim it, and stash the hex content as
// the binding value. The existing validateBinding then rejects anything
// that isn't even-length hex >= 16 chars.
function SystemFingerprintField({
  skuId,
  bindingValue,
  onSet
}: {
  skuId: string;
  bindingValue: string;
  onSet: (hex: string) => void;
}): JSX.Element {
  const [fileName, setFileName] = useState<string>('');
  const [parseError, setParseError] = useState<string>('');
  const onFile = async (file: File): Promise<void> => {
    setFileName(file.name);
    setParseError('');
    const txt = (await file.text()).trim();
    if (!/^[0-9a-fA-F]+$/.test(txt)) {
      setParseError('File contents are not hex -- pick the system.fingerprint file from the Z9/Flex Server install.');
      onSet('');
      return;
    }
    onSet(txt.toLowerCase());
  };
  return (
    <div className="space-y-1 text-sm">
      <label className="block">
        <span className="font-medium text-slate-700">System fingerprint</span>
        <input
          type="file"
          accept=".fingerprint,text/plain,application/octet-stream"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm shadow-sm file:mr-3 file:rounded file:border-0 file:bg-slate-900 file:px-3 file:py-1 file:text-xs file:font-medium file:text-white hover:file:bg-slate-800 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          data-testid={`cart-binding-${skuId}`}
          required
        />
      </label>
      {fileName ? (
        <p className="text-xs text-slate-500" data-testid={`cart-binding-file-${skuId}`}>
          Selected: <span className="font-mono">{fileName}</span>
          {bindingValue ? <> -- {bindingValue.slice(0, 8)}...{bindingValue.slice(-6)}</> : null}
        </p>
      ) : null}
      {parseError ? (
        <p className="text-xs text-red-600" data-testid={`cart-binding-error-${skuId}`}>
          {parseError}
        </p>
      ) : null}
    </div>
  );
}
